import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, GradientButton } from '../../components/common/Primitives';
import { useToast } from '../../hooks/use-toast';
import { BRAND } from '../../lib/mock';
import { CheckCircle2 } from 'lucide-react';

const CONFIG = {
  'request-demo': {
    eyebrow: 'REQUEST A DEMO', title: 'Request an online demonstration',
    desc: 'See the VR modules, school portal, assessments and analytics before deciding on deployment.',
    submitLabel: 'Submit Demo Request', card: 'School and contact details', ackHours: 48,
    fields: [ ['School or institution name','institution',null,true], ['School type','type','select',true,['CBSE','ICSE','State Board','International','University','Coaching Institute','Other']], ['City','city',null,true], ['Website','website'], ['Contact person','contact',null,true], ['Role or designation','role','select',false,['Principal','Vice Principal','Teacher','Coordinator','Owner','Other']], ['Email','email','email',true], ['Phone','phone'] ],
    demoPref: true, dateTime: true, message: 'Optional: tell us what you would like to see in the demo',
  },
  'start-deployment': {
    eyebrow: 'START DEPLOYMENT', title: 'Start a Gyan Dhara deployment discussion',
    desc: 'Share your institution details. The team will guide the hardware, logistics and rollout timeline.',
    submitLabel: 'Submit Deployment Request', card: 'Institution details', ackHours: 72,
    fields: [ ['School or institution name','institution',null,true], ['City','city',null,true], ['Contact person','contact',null,true], ['Role or designation','role'], ['Email','email','email',true], ['Phone','phone'], ['Expected number of students','students'], ['Preferred deployment window','window'] ],
    message: 'Anything else the team should know about your context?',
  },
  'access-request': {
    eyebrow: 'STUDENT + FAMILY ACCESS', title: 'Request family access',
    desc: 'Let the Gyan Dhara team know about your school so that individual student and parent accounts can be enabled.',
    submitLabel: 'Submit Access Request', card: 'Family and school details', ackHours: 48,
    fields: [ ['Parent full name','parent',null,true], ['Student full name','student',null,true], ['Grade','grade','select',true,['8','9']], ["Student's school",'school'], ['City','city'], ['Email','email','email',true], ['Phone','phone'], ['Preferred language','language','select',false,['English','Hindi','Assamese']] ],
    message: 'Anything you would like the team to consider?',
  },
  'collaboration-request': {
    eyebrow: 'RESEARCH COLLABORATION', title: 'Request a research collaboration',
    desc: 'The collaboration request helps the team understand the proposed scope, institution, timeline and expected contribution.',
    submitLabel: 'Submit Collaboration Request', card: 'Collaboration proposal', ackHours: 72,
    fields: [ ['Full name','name',null,true], ['Institution','institution',null,true], ['Role','role'], ['Email','email','email',true], ['Area of interest','area','select',true,['Learning sciences','HCI & accessibility','Data & analytics','Ethics & responsible research','Joint evaluation','Other']], ['Availability','availability'] ],
    message: 'Proposed collaboration scope, methods and expected timeline',
  },
  'adoption-request': {
    eyebrow: 'COACHING ADOPTION', title: 'Request coaching institute adoption',
    desc: 'The team can prepare a customised deployment estimate covering hardware, configuration, courier and optional support.',
    submitLabel: 'Submit Adoption Request', card: 'Coaching institute details', ackHours: 72,
    fields: [ ['Institute name','institute',null,true], ['City','city',null,true], ['Contact person','contact',null,true], ['Role','role'], ['Email','email','email',true], ['Phone','phone'], ['Estimated learner volume','volume'], ['Expected start','start'] ],
    message: 'Session scheduling preferences or specific chapters of interest',
  },
  'partnership-request': {
    eyebrow: 'PARTNERSHIP DISCUSSION', title: 'Discuss a partnership',
    desc: 'Share the type of partnership — public programme, CSR, implementation or strategic — for a tailored conversation.',
    submitLabel: 'Submit Partnership Enquiry', card: 'Partner details', ackHours: 72,
    fields: [ ['Organisation','org',null,true], ['Partnership type','type','select',true,['Public programme','CSR-supported deployment','Implementation partnership','Strategic collaboration']], ['Contact person','contact',null,true], ['Role','role'], ['Email','email','email',true], ['Phone','phone'], ['Region of interest','region'], ['Expected scale','scale'] ],
    message: 'Objectives, timeline and any specific expectations',
  },
};

function Field({ f, onChange }) {
  const [label, name, type = 'text', required = false, options] = f;
  if (type === 'select') {
    return (
      <label className="block"><span className="text-[12.5px] text-ink-500">{label}</span>
        <select onChange={e=>onChange(name,e.target.value)} required={required} className="mt-1 w-full rounded-lg border border-canvas-200 bg-white px-3 py-2.5 text-[13.5px] focus:border-purple">
          <option value="">Select {label.toLowerCase()}</option>
          {(options||[]).map(o=>(<option key={o} value={o}>{o}</option>))}
        </select>
      </label>
    );
  }
  return (
    <label className="block"><span className="text-[12.5px] text-ink-500">{label}</span>
      <input type={type} onChange={e=>onChange(name,e.target.value)} required={required} className="mt-1 w-full rounded-lg border border-canvas-200 px-3 py-2.5 text-[13.5px] focus:border-purple" />
    </label>
  );
}

export default function FormPage({ kind = 'request-demo' }) {
  const cfg = CONFIG[kind];
  const { toast } = useToast();
  const [values, setValues] = useState({});
  const [done, setDone] = useState(false);
  const onChange = (k,v) => setValues(s => ({...s, [k]: v}));

  const submit = (e) => {
    e.preventDefault();
    // MOCKED submission: stores locally & shows confirmation.
    const submissions = JSON.parse(localStorage.getItem('gd_submissions') || '[]');
    submissions.push({ kind, values, at: new Date().toISOString() });
    try { localStorage.setItem('gd_submissions', JSON.stringify(submissions)); } catch {}
    setDone(true);
    toast({ title: 'Submitted (mock)', description: `In the full build this will be emailed to ${BRAND.contactEmail}.` });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageShell>
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1100px]">
          <Eyebrow color="purple">{cfg.eyebrow}</Eyebrow>
          <h1 className="mt-2 text-[38px] md:text-[48px] font-extrabold leading-[1.05] tracking-tight text-navy">{cfg.title}</h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-ink-500">{cfg.desc}</p>
        </div>
      </section>

      <section className="px-6 pb-16">
        <div className="mx-auto max-w-[1100px]">
          {done ? (
            <div className="rounded-3xl bg-brand-gradient p-8 md:p-10 text-white shadow-card">
              <div className="flex items-center gap-3"><CheckCircle2 className="h-8 w-8" /><h3 className="text-[22px] font-extrabold">Request received</h3></div>
              <p className="mt-3 max-w-2xl text-[14px] text-white/85">Your {cfg.eyebrow.toLowerCase()} has been recorded. We will contact you within {cfg.ackHours} hours.</p>
              <div className="mt-6 text-[12.5px] text-white/70">Note: this preview stores your submission in your browser. When the email service is enabled it will be sent to {BRAND.contactEmail}.</div>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-canvas-200 bg-white p-6 md:p-8 shadow-soft">
              <div className="text-[15px] font-bold text-navy">{cfg.card}</div>
              <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
                {cfg.fields.map((f,i)=>(<Field key={i} f={f} onChange={onChange} />))}
              </div>
              {cfg.demoPref && (
                <div className="mt-6">
                  <div className="text-[12.5px] text-ink-500">Demo preference</div>
                  <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {['Online product demo','Temporary pilot','Both'].map(o => (
                      <label key={o} className="flex items-start gap-2 rounded-xl border border-canvas-200 bg-white p-4 cursor-pointer hover:border-purple">
                        <input type="radio" name="pref" value={o} onChange={e=>onChange('preference',e.target.value)} className="mt-1 accent-purple" />
                        <span className="text-[13.5px] font-semibold text-navy">{o}<span className="block text-[12px] font-normal text-ink-500">{o==='Temporary pilot' ? 'Requires an existing headset' : ' '}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {cfg.dateTime && (
                <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
                  <label className="block"><span className="text-[12.5px] text-ink-500">Preferred date</span><input type="date" onChange={e=>onChange('date',e.target.value)} className="mt-1 w-full rounded-lg border border-canvas-200 px-3 py-2.5 text-[13.5px] focus:border-purple" /></label>
                  <label className="block"><span className="text-[12.5px] text-ink-500">Preferred time</span>
                    <select onChange={e=>onChange('time',e.target.value)} className="mt-1 w-full rounded-lg border border-canvas-200 bg-white px-3 py-2.5 text-[13.5px]">
                      <option value="">Select a time window</option>
                      <option>Morning (9–12)</option><option>Afternoon (12–4)</option><option>Evening (4–7)</option>
                    </select>
                  </label>
                </div>
              )}
              <label className="mt-6 block"><span className="text-[12.5px] text-ink-500">Message</span>
                <textarea rows={5} onChange={e=>onChange('message',e.target.value)} placeholder={cfg.message} className="mt-1 w-full rounded-lg border border-canvas-200 px-3 py-2.5 text-[13.5px] focus:border-purple" />
              </label>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <button className="rounded-lg bg-brand-gradient px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-soft hover:brightness-110 transition-all">{cfg.submitLabel}</button>
                <span className="text-[12.5px] text-ink-500">We will contact you within {cfg.ackHours} hours.</span>
              </div>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
}
